import React, { useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, Loader2, Merge, Search, X } from 'lucide-react';
import { mergeProductInto, type DeletionBlocker } from '../../lib/product-deletion';

export interface MergeCandidate {
  id: string;
  name: string;
  sku?: string;
  quantity: number;
}

interface MergeDuplicateModalProps {
  product: MergeCandidate;
  blockers: DeletionBlocker[];
  candidates: MergeCandidate[];
  onClose: () => void;
  onMerged: (summary: { movedSalesRecords: number; movedHistoricalRows: number }) => void;
}

export function MergeDuplicateModal({
  product,
  blockers,
  candidates,
  onClose,
  onMerged,
}: MergeDuplicateModalProps) {
  const [search, setSearch] = useState('');
  const [targetId, setTargetId] = useState<string | null>(null);
  const [isMerging, setIsMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasStock = product.quantity > 0;

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length < 2) return [];

    return candidates
      .filter(candidate => candidate.id !== product.id)
      .filter(candidate =>
        candidate.sku?.toLowerCase().includes(term) || candidate.name?.toLowerCase().includes(term))
      .slice(0, MAX_RESULTS);
  }, [candidates, product.id, search]);

  const target = useMemo(
    () => candidates.find(candidate => candidate.id === targetId) ?? null,
    [candidates, targetId],
  );

  async function handleMerge() {
    if (!target || hasStock) return;

    setIsMerging(true);
    setError(null);

    const result = await mergeProductInto({
      sourceProductId: product.id,
      targetProductId: target.id,
    });

    if (!result.success) {
      setError(result.error ?? 'Failed to merge product');
      setIsMerging(false);
      return;
    }

    onMerged({
      movedSalesRecords: result.movedSalesRecords ?? 0,
      movedHistoricalRows: result.movedHistoricalRows ?? 0,
    });
  }

  return (
    <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-duplicate-title"
        className="relative w-full max-w-2xl shadow-2xl rounded-2xl bg-white dark:bg-gray-800 overflow-hidden"
      >
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-white/20 rounded-lg mr-3">
                <Merge className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 id="merge-duplicate-title" className="text-xl font-semibold text-white">
                  This product has history
                </h3>
                <p className="text-amber-100 text-sm mt-0.5">
                  Merge it into the one you are keeping
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isMerging}
              aria-label="Close"
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
            <p className="text-sm text-amber-900 dark:text-amber-200">
              <span className="font-semibold">{product.sku || product.name}</span> can't be deleted
              because it still has records attached. Deleting it would remove that history.
            </p>
            <ul className="mt-3 space-y-1">
              {blockers.map(blocker => (
                <li
                  key={blocker.table}
                  className="text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                  {blocker.count} {blocker.label}
                </li>
              ))}
            </ul>
          </div>

          {hasStock ? (
            <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">
                This product still holds <span className="font-semibold">{product.quantity} units</span>.
                Move or zero its stock first so the adjustment stays visible in your inventory
                history — a merge never moves stock on its own.
              </p>
            </div>
          ) : (
            <div>
              <label
                htmlFor="merge-target-search"
                className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
              >
                Merge into which product?
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="merge-target-search"
                  type="text"
                  value={search}
                  onChange={event => {
                    setSearch(event.target.value);
                    setTargetId(null);
                  }}
                  placeholder="Search by SKU or name"
                  disabled={isMerging}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                />
              </div>

              {search.trim().length >= 2 && (
                <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  {matches.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                      No other product matches "{search.trim()}"
                    </p>
                  ) : (
                    matches.map(candidate => (
                      <button
                        key={candidate.id}
                        type="button"
                        onClick={() => setTargetId(candidate.id)}
                        disabled={isMerging}
                        aria-pressed={targetId === candidate.id}
                        className={`w-full text-left px-3 py-2.5 transition-colors disabled:opacity-50 ${
                          targetId === candidate.id
                            ? 'bg-amber-50 dark:bg-amber-900/30'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {candidate.sku || '(no SKU)'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {candidate.name} · {candidate.quantity} units
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {target && !hasStock && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {product.sku || product.name}
                </span>
                <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {target.sku || target.name}
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                All history moves to {target.sku || target.name}, then the duplicate is removed.
                Your totals and reports stay unchanged.
              </p>
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isMerging}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Keep both
            </button>
            <button
              type="button"
              onClick={handleMerge}
              disabled={!target || hasStock || isMerging}
              className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {isMerging && <Loader2 className="h-4 w-4 animate-spin" />}
              {isMerging ? 'Merging…' : 'Merge and delete duplicate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const MAX_RESULTS = 50;
